import { Modal, List, Input, Button } from "@arco-design/web-react";
import axios from "axios";

import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useEffect, useState } from "react";
import { User } from "@sendbird/chat/lib/__definition";
import { getButtonColor, getButtonTextColor } from "../../../../utils/cssVariables";
import { endpoints } from "../../../../api/endpoints";

import "./StartConversationDialog.css";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { useTranslation } from "react-i18next";

type MessagingRecipient = {
  id: number;
  name: string;
  last_name?: string;
  avatar_url?: string;
  is_mentor?: boolean;
  is_admin?: boolean;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onMessagingStart: (user: User) => void;
};

function displayName(recipient: MessagingRecipient): string {
  return `${recipient.name || ""} ${recipient.last_name || ""}`.trim();
}

function toSendbirdUser(recipient: MessagingRecipient): User {
  return {
    userId: recipient.id.toString(),
    nickname: displayName(recipient),
    profileUrl: recipient.avatar_url || "",
  } as User;
}

export function StartConversationDialog({
  visible,
  onClose,
  onMessagingStart,
}: Props) {
  const { t } = useTranslation();
  const currentUserID = useSelector(
    (state: RootState) => state.user.user?.id
  );
  const [usersList, setUsersList] = useState<MessagingRecipient[]>([]);
  const [blockedUserIds, setBlockedUserIds] = useState<string[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [isSearching, setIsSearching] = useState(false);

  const {
    stores: {
      sdkStore: { sdk },
    },
  } = useSendbirdStateContext();
  const bUsersQuery = sdk.createBlockedUserListQuery?.();

  useEffect(() => {
    const fetchData = async () => {
      if (!filter || filter.trim().length < 2) {
        setUsersList([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await axios.get(endpoints.messagingRecipients, {
          params: { q: filter.trim() },
        });
        const recipients: MessagingRecipient[] = response.data || [];
        setUsersList(
          recipients.filter((recipient) => recipient.id !== currentUserID)
        );
      } catch (error) {
        console.error(error);
        setUsersList([]);
      } finally {
        setIsSearching(false);
      }
    };

    if (visible) {
      fetchData().catch(console.error);
    } else {
      setUsersList([]);
    }
  }, [filter, visible, currentUserID]);

  useEffect(() => {
    const fetchData = async () => {
      const blockedUsersData: User[] = await bUsersQuery?.next();

      if (blockedUsersData && blockedUsersData.length) {
        setBlockedUserIds(blockedUsersData.map((user) => user.userId));
      }
    };

    if (visible) {
      fetchData().catch(console.error);
    }
  }, [visible]);

  const handleMessagingStart = (recipient: MessagingRecipient) => {
    onMessagingStart(toSendbirdUser(recipient));
    onClose();
    resetForm();
  };

  const handleUnblockAndMessagingStart = (recipient: MessagingRecipient) => {
    const user = toSendbirdUser(recipient);
    sdk.unblockUserWithUserId(user.userId).then(() => {
      onMessagingStart(user);
      onClose();
      resetForm();
    });
  };

  const resetForm = () => {
    setUsersList([]);
    setFilter('');
  }

  const renderUser = (item: MessagingRecipient) => {
    const userId = item.id.toString();
    const isBlocked = blockedUserIds.includes(userId);
    const name = displayName(item);

    return (
      <List.Item
        key={userId}
        actions={[
          <Button
            style={{
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            }}
            size={"mini"}
            shape={"round"}
            onClick={() =>
              isBlocked
                ? handleUnblockAndMessagingStart(item)
                : handleMessagingStart(item)
            }
          >
            {isBlocked ? t('messages.unblock_and_start_messaging') : t('messages.start_messaging') }
          </Button>,
        ]}
      >
        <List.Item.Meta
          title={
            <div>
              {name}
              {item.is_admin && (
                <span className="start-conversation-dialog__role-tag">
                  {t('common.admin')}
                </span>
              )}
              {isBlocked && (
                <span className="start-conversation-dialog__blocked-user">
                  {t('messages.is_blocked')}
                </span>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <Modal
      title={t('messages.choose_user_modal_message')}
      visible={visible}
      onCancel={onClose}
      autoFocus
      focusLock={true}
      className={"start-conversation-dialog"}
      footer={null}
    >
      <div className="start-conversation-dialog__content">
        <Input
          allowClear
          value={filter}
          onChange={setFilter}
          placeholder={t('messages.name_placeholder')}
        />
        <List
          className="list-demo-actions"
          size="small"
          dataSource={usersList}
          loading={isSearching}
          render={renderUser}
          noDataElement={
            <div className="start-conversation-dialog__no-users-message">
              {filter.trim().length < 2
                ? t('messages.find_users_placeholder')
                : t('messages.no_users_found')}
            </div>
          }
        />
      </div>
    </Modal>
  );
}
